import { User } from "../../../../../api/src/types";
import Popup from "../../../components/popups/Popup";
import {
  FLEX_COLUMN,
  FLEX_ROW,
  INFORMATIONAL_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT,
  MOBILE_SCREEN,
} from "../../../components/theme";
import {
  ParagraphHeaderXLarge,
  ParagraphHeaderSmall,
  ParagraphSmall,
} from "../../../components/typography/Paragraph";
import { formatUserFullName, formatUserInitials } from "../../../utils/format";
import styled from "@emotion/styled";
import { navigateToNextSection } from "../utils/navigation";

interface UserConfirmIdentityProps {
  user: User;
  sections: { id: string; order: number; label: string }[];
  currentSection: { id: string; order: number; label: string };
  setUserConfirmIdentityPopupOpen: (open: boolean) => void;
  scrollToIdentity: () => void;
  navigate: (path: string) => void;
}

const TitleContainer = styled.div`
  ${FLEX_COLUMN}
  gap: 16px;
`;

const BodyContainer = styled.div`
  ${FLEX_COLUMN}
  margin-top: 16px;
  gap: 16px;
  width: 100%;
`;

const FooterContainer = styled.div`
  ${FLEX_COLUMN}
  gap: 8px;
`;

const RowContainer = styled.div`
  ${FLEX_ROW}
  gap: 16px;
  justify-content: space-between;
  ${MOBILE_SCREEN} {
    flex-direction: column;
  }
`;

const ItemContainer = styled.div`
  ${FLEX_COLUMN}
  gap: 8px;
  flex: 1;
  text-align: left;
`;

const UserConfirmIdentity: React.FC<UserConfirmIdentityProps> = ({
  user,
  sections,
  currentSection,
  setUserConfirmIdentityPopupOpen,
  navigate,
  scrollToIdentity,
}) => {
  return (
    <Popup
      title={
        <TitleContainer>
          <ParagraphHeaderXLarge>Confirm your identity</ParagraphHeaderXLarge>
          <ParagraphSmall>
            This is how your name will appear in your document, exactly as
            entered, including capitalization.
          </ParagraphSmall>
        </TitleContainer>
      }
      backgroundColor={INFORMATIONAL_MESSAGE_POPUP_BACKGROUND_COLOR_GRADIENT}
      description={
        <BodyContainer>
          <ItemContainer>
            <ParagraphHeaderSmall fontWeight={700}>
              Full name:
            </ParagraphHeaderSmall>
            <ParagraphSmall>{formatUserFullName(user)}</ParagraphSmall>
          </ItemContainer>
          <RowContainer>
            <ItemContainer>
              <ParagraphHeaderSmall fontWeight={700}>
                First name:
              </ParagraphHeaderSmall>
              <ParagraphSmall>{user.firstName}</ParagraphSmall>
            </ItemContainer>
            <ItemContainer>
              <ParagraphHeaderSmall fontWeight={700}>
                Initials:
              </ParagraphHeaderSmall>
              <ParagraphSmall>{formatUserInitials(user)}</ParagraphSmall>
            </ItemContainer>
          </RowContainer>
          <FooterContainer>
            <ParagraphSmall fontWeight={700}>
              Is the above information correct?
            </ParagraphSmall>
            <ParagraphSmall>
              You will not be able to edit this information later.
            </ParagraphSmall>
          </FooterContainer>
        </BodyContainer>
      }
      buttons={{
        confirmText: "Confirm",
        onConfirm: () => {
          setUserConfirmIdentityPopupOpen(false);
          navigateToNextSection(sections, currentSection, navigate);
        },
        cancelText: "Make a change",
        onCancel: () => {
          setUserConfirmIdentityPopupOpen(false);
          scrollToIdentity();
        },
      }}
    />
  );
};

export default UserConfirmIdentity;
